import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Upload, Trash2, Loader2, Film, ShieldCheck, Plus, Save, X,
  Tags, Inbox, ClipboardList, Check, RotateCcw, ChevronDown, ChevronUp,
  Users, Crown, Ban, RefreshCw, Home, FileEdit,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { value: "standard", label: "Standard" },
  { value: "vip", label: "VIP" },
];

const emptyPkg = {
  id: null, name: "", tagline: "", price: "", features: "",
  tier: "standard", sort_order: 99, stripe_link: "", is_new: false, on_sale: false,
};

export default function PricingTab() {
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(null); // pkg object or "new"
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/packages`);
      setPackages(r.data);
    } catch {
      toast.error("Failed to load packages");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (pkg) => {
    setEditing({
      ...pkg,
      features: pkg.features.join("\n"),
      price: String(pkg.price),
      stripe_link: pkg.stripe_link || "",
      is_new: !!pkg.is_new,
      on_sale: !!pkg.on_sale,
    });
  };
  const startNew = () => setEditing({ ...emptyPkg });
  const cancel = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: editing.name.trim(),
      tagline: editing.tagline.trim(),
      price: parseFloat(editing.price) || 0,
      features: editing.features
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      tier: editing.tier,
      sort_order: parseInt(editing.sort_order, 10) || 99,
      stripe_link: (editing.stripe_link || "").trim(),
      is_new: !!editing.is_new,
      on_sale: !!editing.on_sale,
    };
    if (!payload.name) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/packages/${editing.id}`, payload);
        toast.success("Package updated");
      } else {
        await axios.post(`${API}/admin/packages`, payload);
        toast.success("Package created");
      }
      setEditing(null);
      await load();
    } catch (err) {
      {
        toast.error("Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this package? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/packages/${id}`);
      toast.success("Package deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-display font-bold uppercase text-xl">Pricing packages</h2>
          <p className="text-white/50 text-sm mt-1">Edit names, taglines, prices and features. Changes go live instantly.</p>
        </div>
        <button onClick={startNew} className="btn-primary" data-testid="add-package-btn">
          <Plus size={16} /> Add package
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-8" data-testid="admin-package-list">
        {packages.map((p) => (
          <div key={p.id} className="rv-card rounded-xl p-5" data-testid={`admin-pkg-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display uppercase font-bold truncate">{p.name}</div>
                  {p.tier === "vip" && (
                    <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-yellow-500/40 text-yellow-400">VIP</span>
                  )}
                  {p.is_new && (
                    <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--rv-red)] text-white font-bold">NEW</span>
                  )}
                  {p.on_sale && (
                    <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-yellow-500/20 border border-yellow-500/50 text-yellow-300 font-bold">SALE</span>
                  )}
                </div>
                <div className="text-white/55 text-sm mt-1">{p.tagline}</div>
                <div className="font-display text-2xl mt-2">${p.price.toFixed(2)}</div>
                <div className="text-xs text-white/40 mt-2 flex items-center gap-2 flex-wrap">
                  <span>{p.features.length} features · sort: {p.sort_order}</span>
                  {p.stripe_link ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[9px] uppercase tracking-widest">Stripe link set</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/50 text-[9px] uppercase tracking-widest">Built-in checkout</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button
                  onClick={() => startEdit(p)}
                  className="text-xs uppercase tracking-widest border border-white/15 rounded px-3 py-1.5 hover:border-[var(--rv-red)] hover:text-[var(--rv-red)] transition"
                  data-testid={`edit-pkg-${p.id}`}
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(p.id)}
                  className="text-xs uppercase tracking-widest text-white/55 hover:text-[var(--rv-red)] inline-flex items-center gap-1.5 justify-center"
                  data-testid={`delete-pkg-${p.id}`}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={cancel}
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="max-w-2xl w-full bg-[#0a0a0a] border border-white/10 rounded-xl p-7 relative max-h-[90vh] overflow-y-auto"
            data-testid="package-edit-form"
          >
            <button
              type="button"
              onClick={cancel}
              className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/70 border border-white/15 flex items-center justify-center hover:bg-[var(--rv-red)] transition"
              data-testid="close-edit-modal"
            >
              <X size={18} />
            </button>
            <h3 className="font-display font-black uppercase text-2xl">
              {editing.id ? "Edit package" : "New package"}
            </h3>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Name</Label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  required
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="pkg-name-input"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Tagline</Label>
                <Input
                  value={editing.tagline}
                  onChange={(e) => setEditing({ ...editing, tagline: e.target.value })}
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="pkg-tagline-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Price (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editing.price}
                  onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="pkg-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Tier</Label>
                <select
                  value={editing.tier}
                  onChange={(e) => setEditing({ ...editing, tier: e.target.value })}
                  className="w-full h-10 bg-black/40 border border-white/10 text-white rounded-md px-3 text-sm"
                  data-testid="pkg-tier-input"
                >
                  {TIERS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Features (one per line)</Label>
                <Textarea
                  rows={6}
                  value={editing.features}
                  onChange={(e) => setEditing({ ...editing, features: e.target.value })}
                  className="bg-black/40 border-white/10 text-white font-mono text-sm"
                  data-testid="pkg-features-input"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Stripe payment link</Label>
                <Input
                  type="url"
                  placeholder="https://buy.stripe.com/..."
                  value={editing.stripe_link || ""}
                  onChange={(e) => setEditing({ ...editing, stripe_link: e.target.value })}
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="pkg-stripe-link-input"
                />
                <p className="text-[10px] text-white/40 uppercase tracking-widest">Paste a Stripe Payment Link to redirect Buy Now directly. Leave blank to use the built-in checkout.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Sort order</Label>
                <Input
                  type="number"
                  value={editing.sort_order}
                  onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })}
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="pkg-sort-input"
                />
                <p className="text-[10px] text-white/40 uppercase tracking-widest">Lower numbers show first. VIP is rendered separately.</p>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Promo flags</Label>
                <div className="flex gap-3 flex-wrap">
                  <label
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition select-none ${
                      editing.is_new
                        ? "border-[var(--rv-red)]/60 bg-[var(--rv-red)]/10"
                        : "border-white/10 hover:border-white/25"
                    }`}
                    data-testid="pkg-is-new-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={!!editing.is_new}
                      onChange={(e) => setEditing({ ...editing, is_new: e.target.checked })}
                      className="accent-[var(--rv-red)] h-4 w-4"
                    />
                    <div>
                      <div className="font-display uppercase tracking-tight text-sm">NEW badge</div>
                      <div className="text-[10px] text-white/45">Red “NEW” pill in the card&apos;s top right corner.</div>
                    </div>
                  </label>

                  <label
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition select-none ${
                      editing.on_sale
                        ? "border-yellow-500/60 bg-yellow-500/10"
                        : "border-white/10 hover:border-white/25"
                    }`}
                    data-testid="pkg-on-sale-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={!!editing.on_sale}
                      onChange={(e) => setEditing({ ...editing, on_sale: e.target.checked })}
                      className="accent-yellow-400 h-4 w-4"
                    />
                    <div>
                      <div className="font-display uppercase tracking-tight text-sm">SALE animation</div>
                      <div className="text-[10px] text-white/45">Animated glowing border + “SALE” ribbon.</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center" data-testid="save-pkg-btn">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} /> Save package</>}
              </button>
              <button type="button" onClick={cancel} className="btn-ghost" data-testid="cancel-edit-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


